<template>
  <div>
    <CRow>
      <CCol>
        <div class="mb-2">
          <div class="font-weight-bold">{{ title }}</div>
        </div>
        <div class="mb-2">
          <CButton
              color="dark"
              @click="openAdd"
              class="w-25"
              :disabled="maxRows !== null && localData.length >= maxRows"
          >
            Add
          </CButton>
        </div>
      </CCol>
    </CRow>
    <CRow>
      <CCol>
    <table class="table table-bordered table-hover table-sm mb-0">
      <thead class="thead-light">
      <tr>
        <th v-for="col in columns" :key="col.key">
          {{ col.label }}
        </th>
        <th style="width:120px" class="text-center">Action</th>
      </tr>
      </thead>

      <tbody>
      <tr v-for="(row, index) in localData" :key="index">
        <td v-for="col in columns" :key="col.key">
            <span v-if="col.type !== 'image'">
              {{ row[col.key] }}
            </span>
          <img
              v-else-if="col.type === 'image' && row[col.key]"
              :src="resolveImage(row[col.key])"
              style="max-width:80px;max-height:80px"
              alt=""
          />

        </td>

        <td class="text-center">
          <CButton size="sm" color="light" class="mr-1" @click="openEdit(index)">
            Edit
          </CButton>
          <CButton size="sm" color="danger" variant="outline" @click="remove(index)">
            Delete
          </CButton>
        </td>
      </tr>

      <tr v-if="localData.length === 0">
        <td :colspan="columns.length + 1" class="text-center">
          No data
        </td>
      </tr>
      </tbody>
    </table>
      </CCol>
    </CRow>
    <CModal
        :show.sync="showModal"
        centered
        :title="isEdit ? 'Edit Data' : 'Add Data'"
        :hide-footer="true"
    >
      <CModalBody>
        <CRow>
          <CCol sm="12">
            <div v-for="col in columns" :key="col.key" class="form-group mb-4">
              <CLabel>{{ col.label }}</CLabel>

              <CInput
                  v-if="col.type === 'number'"
                  type="number"
                  v-model.number="form[col.key]"
              />

              <CTextarea
                  v-else-if="col.type === 'textarea'"
                  v-model="form[col.key]"
                  :maxlength="col.maxlength"
                  rows="4"
              />

              <CInput
                  v-else-if="col.type === 'text'"
                  v-model="form[col.key]"
                  :maxlength="col.maxlength"
              />

              <div v-else-if="col.type === 'image'">
                <input
                    :key="fileInputKey + col.key"
                    type="file"
                    accept="image/*"
                    @change="onFileChange($event, col.key)"
                    class="form-control-file mb-3"
                />

                <div v-if="imagePreview[col.key]" class="mt-3">
                  <img
                      :src="imagePreview[col.key]"
                      class="img-preview"
                      alt="Preview"
                  />
                </div>
              </div>

              <CInput
                  v-else
                  v-model="form[col.key]"
              />
            </div>
          </CCol>
        </CRow>
      </CModalBody>
      <template #footer>
        <div class="d-flex justify-content-between w-100">
          <CButton color="secondary" @click="showModal = false">
            Back
          </CButton>
          <CButton color="dark" :disabled="isSaving" @click="save">
            {{ isEdit ? 'Update' : 'Save' }}
          </CButton>
        </div>
      </template>
    </CModal>
  </div>
</template>

<script>
export default {
  name: "DynamicCrudTable",
  props: {
    title: { type: String, default: "Data" },
    columns: { type: Array, required: true },
    value: { type: Array, default: () => [] },
    maxRows: { type: Number, default: null }
  },
  data() {
    return {
      localData: [...this.value],
      showModal: false,
      isEdit: false,
      editIndex: null,
      form: {},
      fileInputKey: 0,
      imagePreview: {},
      isSaving: false
    };
  },
  watch: {
    value(val) {
      this.localData = [...val];
    }
  },
  methods: {
    openAdd() {
      if (this.maxRows !== null && this.localData.length >= this.maxRows) {
        return;
      }
      this.isSaving = false;
      this.isEdit = false;
      this.editIndex = null;
      this.initForm();
      this.imagePreview = {};
      this.fileInputKey++;
      this.showModal = true;
    },

    openEdit(index) {
      this.isSaving = false;
      this.isEdit = true;
      this.editIndex = index;

      // reset
      this.initForm()
      this.imagePreview = {}

      // clone data
      this.form = { ...this.localData[index] }

      // 🔥 rebuild preview dari File
      this.columns.forEach(col => {
        if (col.type === 'image') {
          const value = this.form[col.key]

          if (value instanceof File) {
            // revoke lama (jaga memory)
            if (this.imagePreview[col.key]?.startsWith('blob:')) {
              URL.revokeObjectURL(this.imagePreview[col.key])
            }

            this.$set(
                this.imagePreview,
                col.key,
                URL.createObjectURL(value)
            )
          } else if (typeof value === 'string') {
            this.$set(this.imagePreview, col.key, value)
          }
        }
      })

      this.fileInputKey++
      this.showModal = true
    },

    save() {
      if (this.isSaving || !this.showModal) {
        return;
      }
      this.isSaving = true;

      if (this.isEdit) {
        this.localData.splice(this.editIndex, 1, { ...this.form });
      } else {
        if (this.maxRows !== null && this.localData.length >= this.maxRows) {
          this.isSaving = false;
          return;
        }
        this.localData.push({ ...this.form });
      }
      this.$emit("input", [...this.localData]);
      this.showModal = false;
      this.isSaving = false;
    },

    remove(index) {
      this.localData.splice(index, 1);
      this.$emit("input", [...this.localData]);
    },

    initForm() {
      this.form = {};
      this.columns.forEach(col => {
        this.form[col.key] = "";
      });
    },

    onFileChange(e, key) {
      const file = e.target.files?.[0]
      if (!file) return

      this.$set(this.form, key, file)

      if (this.imagePreview[key]?.startsWith('blob:')) {
        URL.revokeObjectURL(this.imagePreview[key])
      }

      this.$set(this.imagePreview, key, URL.createObjectURL(file))
    },
    resolveImage(value) {
      if (!value) return ""

      if (value instanceof File) {
        return URL.createObjectURL(value)
      }
      return value
    },


  }
};
</script>

<style>
img {
  max-height: 80px;
}

.img-preview{
  width: 100%;
  max-height: 200px;
  object-fit: cover;
}
</style>
